import React, { ReactElement, ReactNode, useMemo, ChangeEvent, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import cx from 'classnames'
import { Checkbox, Select } from '../FormElements'
import { IconAction } from '../Icon'
import './style.sass'
import type { PaginationParams } from 'types/api/requests/common'
import { ButtonPropsObject } from 'website/BulkMoneySender/types'

export const DataTable = ({ children }: { children: ReactNode }) => {
	return <>{children}</>
}

type Evt = {
	target: {
		value: string
	}
}
export type DataTableHeaderReturnType = {
	onSearch: (e: Evt) => void
}

type FooterProps = {
	params: PaginationParams
	pageBack: () => void
	pageForth: () => void
	onChangeCurrentRow?: (e: ChangeEvent<HTMLSelectElement>) => void
	showRowsPerPage?: boolean
	rowsPerPageOptions?: number[]
	FooterActions?: ({
		prevButtonProps,
		nextButtonProps,
	}: {
		prevButtonProps?: ButtonPropsObject
		nextButtonProps?: ButtonPropsObject
	}) => ReactElement<any>
}

interface Query {
	search: string
}

interface Query {
	search: string
}

export const DataTableHeader = (props: {
	onChange: (key: string, query: Query) => void
	render?: ({ onSearch }: DataTableHeaderReturnType) => JSX.Element
}): ReactElement<any> => {
	const onSearch = (e: Evt) => {
		const searchQuery = e.target.value
		// On change should call the API and apply selected query params
		props.onChange('search', { search: searchQuery })
	}
	//@ts-ignore
	return props.render({
		onSearch,
	})
}

type DataTableContentProps = {
	className?: string
	columns: Array<{ name: string }>
	sort?: { sortBy?: Object; sortDirection?: Object }
	children: ReactNode
	footerProps: null | FooterProps
	selectionProps: {
		selectAllInPage: (e: ChangeEvent<HTMLInputElement>) => void
		ariaLabel: string
	} | null
}

export const DataTableContent = (props: DataTableContentProps): ReactElement<'div'> => {
	const { className = '', footerProps } = props
	const cs = cx('DataTableContainer', className)

	return (
		<div className={cs}>
			<table className='DataTable'>
				<thead className='DataTable__header'>
					<tr>
						{props.selectionProps && (
							<td className='DataTable__header-item DataTable__header-item--selection'>
								<Checkbox
									onChange={props.selectionProps.selectAllInPage}
									aria-label={props.selectionProps.ariaLabel}
								/>
							</td>
						)}
						{props.columns.map(column => {
							return (
								<th className='DataTable__header-item' key={column.name}>
									{column.name}
								</th>
							)
						})}
					</tr>
				</thead>

				<tbody data-initial='hidden' data-animate='visible' className='DataTable__content'>
					{props.children}
				</tbody>
			</table>
			{footerProps && (
				<TableFooter
					params={footerProps.params}
					FooterActions={footerProps.FooterActions}
					onChangeCurrentRow={footerProps.onChangeCurrentRow}
					pageBack={footerProps.pageBack}
					pageForth={footerProps.pageForth}
					showRowsPerPage={footerProps.showRowsPerPage}
					rowsPerPageOptions={footerProps.rowsPerPageOptions}
				/>
			)}
		</div>
	)
}

const TableFooter = (props: FooterProps) => {
	const { showRowsPerPage = true, rowsPerPageOptions = [10, 25, 50], onChangeCurrentRow = () => {}, FooterActions } = props
	const { t } = useTranslation(['ui-common/table'])
	return (
		<div>
			<div className='DataTableFooter'>
				<div className='DataTableFooter__paginationContainer'>
					<div className='DataTableFooter__pagination_icons'>
						<IconAction
							withSiblings
							height={18}
							name='chevron-left'
							aria-label={t('footer.actions.previousPage.label')}
							className='Footer__Pagination__Icon'
							onClick={props.pageBack}
							disabled={!Boolean(props.params.prev_page)}
						/>
						<IconAction
							withSiblings
							height={18}
							name='chevron-right'
							aria-label={t('footer.actions.nextPage.label')}
							className='Footer__Pagination__Icon'
							onClick={props.pageForth}
							disabled={!Boolean(props.params.next_page)}
						/>
					</div>
					<span>
						{t('footer.actions.currentPage.beforeDivider')}{' '}
						<span className='DataTableFooter__page'>{props.params.page}</span>{' '}
						{t('footer.actions.currentPage.afterDivider')}{' '}
						<span className='DataTableFooter__page'>{props.params.num_pages}</span>
					</span>
				</div>
				{showRowsPerPage && (
					<div className='DataTableFooter__RowsCustomizer'>
						<Select
							standalone
							value={props.params.per}
							aria-label={t('footer.actions.rowsPerPage.ariaLabel')}
							className='DataTableFooter__RowsCustomizer__SelectBox'
							onChange={onChangeCurrentRow}>
							{rowsPerPageOptions.map((row, idx) => (
								<option key={`${row}-${idx}`} value={row}>
									{row}
								</option>
							))}
						</Select>
						<p className='DataTableFooter__RowsCustomizer__Title'>{t('footer.actions.rowsPerPage.label')}</p>
					</div>
				)}
			</div>
			<div className='DataTableFooter__footerActions'>{FooterActions && <FooterActions />}</div>
		</div>
	)
}

type MobileCheckBoxProps = {
	selectAllInPage: ((e: ChangeEvent<HTMLInputElement>) => void) | boolean
	ariaLabel: string
	label: string
} | null

type DataTableMobileActionsType = {
	pageBack: () => void
	pageForth: () => void
	className?: string
	checkboxProps: MobileCheckBoxProps
	params: PaginationParams
}

export const DataTableMobileActions = ({
	pageBack,
	pageForth,
	className = '',
	checkboxProps,
	params,
}: DataTableMobileActionsType): ReactElement<'div'> => {
	const cs = cx('DataTableMobileActionsContainer', className, {
		'DataTableMobileActionsContainer--nocheckbox': !checkboxProps,
	})
	const { t } = useTranslation(['ui-common/table'])

	return (
		<div className={cs}>
			{checkboxProps && (
				<div>
					<Checkbox
						onChange={checkboxProps.selectAllInPage}
						label={
							<>
								<p className='DataTableMobileActionsContainer__Checkbox__Label'>{checkboxProps.label}</p>
							</>
						}
						aria-label={checkboxProps.ariaLabel}
					/>
				</div>
			)}
			<div className='DataTableMobileActionsContainer--Pagination Pagination'>
				<IconAction
					aria-label={t('footer.actions.previousPage.label')}
					disabled={!Boolean(params.prev_page)}
					name='chevron-left'
					className='Pagination__Icon Footer__Pagination__Icon'
					onClick={pageBack}
				/>
				<IconAction
					disabled={!Boolean(params.next_page)}
					name='chevron-right'
					className='Pagination__Icon Footer__Pagination__Icon'
					onClick={pageForth}
					aria-label={t('footer.actions.nextPage.label')}
				/>
				<p className='Pagination__Content'>
					{t('footer.actions.currentPage.beforeDivider')}{' '}
					<span className='Pagination__Content__Span'>{params.page}</span>{' '}
					{t('footer.actions.currentPage.afterDivider')}{' '}
					<span className='Pagination__Content__Span'>{params.num_pages}</span>
				</p>
			</div>
		</div>
	)
}

type DataTableFooterActionsType = {
	resetSelected: () => void
	className?: string
	children: ReactNode
}
export const DataTableFooterActions = ({
	resetSelected,
	className = '',
	children,
}: DataTableFooterActionsType): ReactElement<'div'> => {
	const cs = cx('FooterActions--container', className)
	return (
		<div className={cs}>
			<IconAction name='close' className='FooterActions--container--close' onClick={resetSelected}></IconAction>
			{children}
		</div>
	)
}

type DataTableDesktopRowType = {
	children: ReactNode
	error?: boolean
	className?: string
	rest?: Object
}

export const DataTableDesktopRow = ({
	children,
	error = false,
	className = '',
	...rest
}: DataTableDesktopRowType): ReactElement<'tr'> => {
	const cs: string = cx('DataTable__content--common', className, {
		'DataTable__content-row': !error,
		'DataTable__content-error': error,
	})
	return (
		<tr {...rest} className={cs}>
			{children}
		</tr>
	)
}

export const DataTableDesktopTableData = ({
	children,
	className,
	blue,
	callback,
	...rest
}: {
	children: ReactNode
	blue?: boolean
	rest?: Object
	className?: string
	callback?: (obj: any) => void
}): ReactElement<'td'> => {
	const cs = cx('DataTable__content-item', className, {
		'DataTable__content-item-blue': blue,
	})

	const isNodeValueBlank = useMemo(() => {
		if (children == null || children === '') return true
		return false
	}, [children])

	const contentToBeRendered = useMemo(() => {
		if (typeof children === 'string') {
			return children.length > 50
				? {
						children: children.substring(0, 50) + '...',
						title: children,
					}
				: {
						children,
						title: '',
					}
		}
		return {
			children,
			title: '',
		}
	}, [children])
	return (
		<td {...rest} title={contentToBeRendered.title} className={cs} onClick={callback}>
			{isNodeValueBlank ? <>{`\u2014`}</> : contentToBeRendered.children}{' '}
		</td>
	)
}
export const DataTableDesktopActions = ({ children }: { children: ReactNode }): ReactElement<'td'> => (
	<td className='DataTable__content-item'>
		<div className='DataTable__hoverActions'>{children}</div>
	</td>
)

type MobileDataContainerType = {
	children: ReactNode
	footerProps: null | FooterProps
	selectionProps: MobileCheckBoxProps
	params: PaginationParams
	className?: string
	mobileActionsClassName?: string
}
export const MobileDataContainer = ({
	children,
	className,
	footerProps,
	selectionProps,
	mobileActionsClassName,
}: MobileDataContainerType): ReactElement<Iterable<ReactNode>> => {
	const cs: string = cx('MobileListContainer', className)
	const mobileNavClassName: string = cx('MobileListContainer__ActionsContainer', mobileActionsClassName)
	return (
		<>
			{footerProps && (
				<div className={mobileNavClassName}>
					<DataTableMobileActions
						params={footerProps.params}
						checkboxProps={selectionProps}
						pageBack={footerProps.pageBack}
						pageForth={footerProps.pageForth}
					/>
				</div>
			)}
			<ul className={cs}>{children}</ul>
			{footerProps && (
				<TableFooter
					params={footerProps.params}
					FooterActions={footerProps.FooterActions}
					onChangeCurrentRow={footerProps.onChangeCurrentRow}
					pageBack={footerProps.pageBack}
					pageForth={footerProps.pageForth}
					rowsPerPageOptions={footerProps.rowsPerPageOptions}
					showRowsPerPage={footerProps.showRowsPerPage}
				/>
			)}
		</>
	)
}

type DataTableMobileDataRowType = {
	className?: string
	tableDataClassName?: string
	error?: boolean
	header?: ReactNode
	body?: ReactNode
	subinfo?: ReactNode
	checked?: boolean
	callback?: (obj: any) => void
	hideCheckbox?: boolean
	checkboxAriaLabel?: string
	checkboxProps: {
		ariaLabel: string
		handleToggleItem: (e: ChangeEvent<HTMLInputElement>) => void
	} | null
}

export const DataTableMobileDataRow = ({
	className = '',
	tableDataClassName = '',
	error = false,
	header,
	body,
	subinfo,
	checked,
	callback = () => () => {},
	hideCheckbox,
	checkboxProps,
}: DataTableMobileDataRowType): ReactElement<'li'> => {
	const cs: string = cx('DataTable__content--common DataTable__content--mobile', className, {
		'DataTable__content-row': !Boolean(error),
		'DataTable__content-error': Boolean(error),
	})

	const tableDataContentClassName = cx('DataTable__content--container', tableDataClassName)

	return (
		<li className={cs}>
			{checkboxProps && (
				<div className='DataTable__content--mobileCheckbox'>
					<Checkbox aria-label={checkboxProps.ariaLabel} checked={checked} onChange={checkboxProps.handleToggleItem} />
				</div>
			)}
			<div style={{ width: '100%' }} className={tableDataContentClassName} onClick={callback}>
				<div className='DataTable__content--mobileContentContainer'>
					{header}
					<p className='DataTable__content--mobileContentContainer--body'>{body}</p>
					<p>{subinfo}</p>
				</div>
			</div>
		</li>
	)
}

export const DataTableMobileBoldHeader = ({ children, error = false, ...rest }: DataTableDesktopRowType): ReactElement<'b'> => {
	const cs: string = cx('DataTable__content--boldheader', {
		'DataTable__content--boldheader--error': Boolean(error),
	})
	return (
		<b className={cs} {...rest}>
			{children}
		</b>
	)
}
